import { useState, type FormEvent } from "react";
import { Check, Download, LoaderCircle } from "lucide-react";
import {
  DESKTOP_RELEASE_URL,
  desktopDownloadUrl,
  desktopPlatformName,
  detectDesktopPlatform,
  type DesktopPlatform
} from "../desktop-release.ts";
import { DesktopPlatformPicker } from "../download/DesktopPlatformPicker.tsx";
const PASSWORD_REQUIREMENT =
  "密码需为 12 至 128 位，并包含小写字母、大写字母、数字和特殊字符（!@%&*_-+=?）";

type SubmissionState = "idle" | "submitting" | "success" | "error";

interface RegistrationResponse {
  registered: true;
  downloadUrl: string;
}

export function InternalRegistrationCta() {
  const [detectedPlatform] = useState(() => detectDesktopPlatform());
  const [platform, setPlatform] = useState<DesktopPlatform>(
    () => detectedPlatform ?? "macos-arm64"
  );
  const [nickname, setNickname] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [state, setState] = useState<SubmissionState>("idle");
  const [errorMessage, setErrorMessage] = useState("");

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (password !== confirmation) {
      setState("error");
      setErrorMessage("两次输入的密码不一致");
      return;
    }
    const passwordError = validatePassword(password);
    if (passwordError) {
      setState("error");
      setErrorMessage(passwordError);
      return;
    }
    setState("submitting");
    setErrorMessage("");
    try {
      const response = await fetch("/v1/product-account-registrations", {
        method: "POST",
        headers: { accept: "application/json", "content-type": "application/json" },
        body: JSON.stringify({
          displayName: nickname.trim(),
          email: email.trim().toLowerCase(),
          password
        })
      });
      if (response.status === 409) {
        throw new Error("该邮箱已有账号，可直接下载安装");
      }
      if (!response.ok) throw new Error("账号创建失败，请稍后重试");
      const result = (await response.json()) as RegistrationResponse;
      if (!result.registered || result.downloadUrl !== DESKTOP_RELEASE_URL) {
        throw new Error("账号已创建，但下载地址不可用");
      }
      setState("success");
      startDesktopDownload(desktopDownloadUrl(platform, result.downloadUrl));
    } catch (error) {
      setState("error");
      setErrorMessage(error instanceof Error ? error.message : "账号创建失败，请稍后重试");
    }
  };

  return (
    <section className="internal-registration" id="download" aria-labelledby="register-title">
      <div className="internal-registration-inner">
        <div className="internal-registration-copy">
          <p>MATTERLINE OFFICE</p>
          <h2 id="register-title">创建账号，开始协作</h2>
          <span>使用公司邮箱注册 Matterline，桌面应用会在账号创建完成后自动下载。</span>
          <DesktopPlatformPicker
            value={platform}
            detectedPlatform={detectedPlatform}
            onChange={setPlatform}
          />
          <a className="internal-download-link" href={desktopDownloadUrl(platform)} download>
            <Download aria-hidden="true" />
            已有账号，下载 {desktopPlatformName(platform)} 版
          </a>
        </div>

        <form className="internal-registration-form" onSubmit={(event) => void submit(event)}>
          <label htmlFor="register-nickname">昵称</label>
          <input
            id="register-nickname"
            type="text"
            autoComplete="nickname"
            minLength={1}
            maxLength={256}
            required
            value={nickname}
            disabled={state === "submitting"}
            onChange={(event) => setNickname(event.target.value)}
          />

          <label htmlFor="register-email">公司邮箱</label>
          <input
            id="register-email"
            type="email"
            inputMode="email"
            autoComplete="email"
            maxLength={320}
            required
            value={email}
            disabled={state === "submitting"}
            onChange={(event) => setEmail(event.target.value)}
          />

          <div className="internal-registration-passwords">
            <label>
              <span>密码</span>
              <input
                type="password"
                autoComplete="new-password"
                minLength={12}
                maxLength={128}
                title={PASSWORD_REQUIREMENT}
                required
                value={password}
                disabled={state === "submitting"}
                onChange={(event) => setPassword(event.target.value)}
              />
            </label>
            <label>
              <span>确认密码</span>
              <input
                type="password"
                autoComplete="new-password"
                minLength={12}
                maxLength={128}
                title={PASSWORD_REQUIREMENT}
                required
                value={confirmation}
                disabled={state === "submitting"}
                onChange={(event) => setConfirmation(event.target.value)}
              />
            </label>
          </div>

          <p className="internal-registration-status" data-state={state} aria-live="polite">
            {state === "success" ? (
              <>
                <Check aria-hidden="true" />
                账号已创建，下载已开始
              </>
            ) : state === "error" ? (
              errorMessage
            ) : (
              PASSWORD_REQUIREMENT
            )}
          </p>

          <button type="submit" disabled={state === "submitting"}>
            {state === "submitting" ? (
              <LoaderCircle className="internal-registration-spinner" aria-hidden="true" />
            ) : (
              <Download aria-hidden="true" />
            )}
            注册并下载 {desktopPlatformName(platform)} 版
          </button>
        </form>
      </div>
    </section>
  );
}

function validatePassword(password: string): string | null {
  if (password.length < 12 || password.length > 128) {
    return "密码长度需为 12 至 128 位";
  }

  const missing: string[] = [];
  if (!/[a-z]/u.test(password)) missing.push("小写字母");
  if (!/[A-Z]/u.test(password)) missing.push("大写字母");
  if (!/[0-9]/u.test(password)) missing.push("数字");
  if (!/[!@%&*_+=?-]/u.test(password)) missing.push("特殊字符（!@%&*_-+=?）");

  return missing.length > 0 ? `密码还需包含：${missing.join("、")}` : null;
}

function startDesktopDownload(url: string): void {
  const link = document.createElement("a");
  link.href = url;
  link.download = "";
  link.hidden = true;
  document.body.append(link);
  link.click();
  link.remove();
}
